/**
 * The table client: one socket, a heartbeat, and a reconnect policy keyed on the
 * close code.
 *
 * Two rules run through all of it.
 *
 * 1. **A `view` replaces local state. It is never merged.** A player who taps a
 *    die on an already-half-open socket sees their own optimistic screen while
 *    the frame dies in a kernel buffer. If the client merged, they would sit
 *    there forever while seven people waited. The server is the truth even when
 *    the truth snaps backwards.
 * 2. **Reconnect is not one undifferentiated ladder.** A deploy (1012) comes
 *    back in seconds; a removal (4001) never does. Retrying both identically
 *    makes a removed player reconnect forever and a deploy look like a dead
 *    network.
 */

import {
  CLOSE,
  ERROR_TEXT,
  PING_INTERVAL_MS,
  REJOIN_RETRY_MS,
  type ClientMsg,
  type ErrorCode,
  type RoomView,
  type ServerMsg,
} from '@shared/protocol';

export type ConnStatus =
  | { kind: 'connecting' }
  | { kind: 'naming'; code: string | null; error: ErrorCode | null; suggestion: string | null }
  | { kind: 'open' }
  | { kind: 'reconnecting' }
  /** The server said it is going down. It will be back; the seat is held. */
  | { kind: 'restarting' }
  /** Over for this client, and no retry will change that. */
  | { kind: 'ended'; reason: 'removed' | 'roomGone' | 'replaced'; lastView: RoomView | null }
  /** Joined too late; we keep trying, because "no" with no "when" is where people quit. */
  | { kind: 'waitingForNextGame'; code: string };

export interface RoomClientEvents {
  onView: (view: RoomView) => void;
  onStatus: (status: ConnStatus) => void;
  onError: (code: ErrorCode, message: string) => void;
  onJoined: (code: string, playerId: string) => void;
}

const tokenKey = (code: string) => `farkle:token:${code}`;
const NAME_KEY = 'farkle:name';

export function storedName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

export function rememberName(name: string) {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    /* private mode: the app works, it just forgets */
  }
}

function readToken(code: string): string | undefined {
  try {
    return localStorage.getItem(tokenKey(code)) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeToken(code: string, token: string) {
  try {
    localStorage.setItem(tokenKey(code), token);
  } catch {
    /* ignore */
  }
}

export function clearToken(code: string) {
  try {
    localStorage.removeItem(tokenKey(code));
  } catch {
    /* ignore */
  }
}

function socketUrl(): string {
  const secure = location.protocol === 'https:';
  const local = /^(localhost|127\.0\.0\.1|\[::1\]|(\d+\.){3}\d+)$/.test(location.hostname);
  // Never speak plaintext to a real host: if the page came over https the socket
  // is wss, and ws:// is only ever used for a local dev server.
  if (!secure && !local) return `wss://${location.host}/ws`;
  return `${secure ? 'wss' : 'ws'}://${location.host}/ws`;
}

export class RoomClient {
  private sock: WebSocket | null = null;
  private heartbeat: number | null = null;
  private watchdog: number | null = null;
  private retryTimer: number | null = null;
  private attempt = 0;
  private lastPongAt = 0;
  private closedByUs = false;

  code: string | null = null;
  playerId: string | null = null;
  view: RoomView | null = null;
  private name = '';
  private intent: 'create' | 'join' = 'join';

  constructor(private readonly ev: RoomClientEvents) {}

  /** Start a table, or join one. The name screen calls this. */
  begin(intent: 'create' | 'join', name: string, code: string | null) {
    this.intent = intent;
    this.name = name;
    this.code = code;
    this.attempt = 0;
    rememberName(name);
    this.open();
  }

  private open() {
    // Before anything else: the old socket's onclose is about to be detached,
    // and it was the only thing that stopped these.
    this.stopHeartbeat();
    this.lastPongAt = Date.now();
    const prev = this.sock;
    this.sock = null;
    if (prev) {
      prev.onclose = null;
      prev.onmessage = null;
      prev.onerror = null;
      prev.onopen = null;
      try {
        prev.close();
      } catch {
        /* already gone */
      }
    }
    this.closedByUs = false;
    this.ev.onStatus({ kind: this.view ? 'reconnecting' : 'connecting' });
    let sock: WebSocket;
    try {
      sock = new WebSocket(socketUrl());
    } catch {
      this.scheduleRetry();
      return;
    }
    this.sock = sock;

    sock.onopen = () => {
      this.attempt = 0;
      this.lastPongAt = Date.now();
      if (this.intent === 'create' && !this.code) {
        this.send({ t: 'create', name: this.name });
      } else if (this.code) {
        this.send({ t: 'join', code: this.code, name: this.name, token: readToken(this.code) });
      }
      this.startHeartbeat();
    };

    sock.onmessage = (ev) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      this.receive(msg);
    };

    sock.onerror = () => {
      /* onclose always follows; the ladder lives there */
    };

    sock.onclose = (ev) => {
      this.stopHeartbeat();
      if (this.closedByUs) return;
      switch (ev.code) {
        case CLOSE.KICKED:
          this.terminal('removed');
          return;
        case CLOSE.SESSION_REPLACED:
          this.terminal('replaced');
          return;
        case CLOSE.NEVER_JOINED:
          this.terminal('roomGone');
          return;
        case CLOSE.SERVICE_RESTART:
          // Back in seconds. Retry almost immediately, with jitter so eight
          // clients don't stampede the same instant.
          this.attempt = 0;
          this.scheduleRetry(400);
          return;
        default:
          this.scheduleRetry();
      }
    };
  }

  private receive(msg: ServerMsg) {
    switch (msg.t) {
      case 'pong':
        this.lastPongAt = Date.now();
        return;

      case 'joined':
        this.code = msg.code;
        this.playerId = msg.playerId;
        writeToken(msg.code, msg.token);
        this.view = msg.view;
        this.ev.onJoined(msg.code, msg.playerId);
        this.ev.onStatus({ kind: 'open' });
        this.ev.onView(msg.view);
        return;

      case 'view':
        this.view = msg.view;
        this.ev.onStatus({ kind: 'open' });
        this.ev.onView(msg.view);
        return;

      case 'error':
        this.onServerError(msg.code, msg.message);
        return;
    }
  }

  private onServerError(code: ErrorCode, message: string) {
    switch (code) {
      case 'ILLEGAL_MOVE':
      case 'WRONG_PHASE':
      case 'NOT_YOUR_TURN':
        // Usually benign: a reconnect resent a move the server already applied,
        // or two taps raced the turn forward. The next view is the truth.
        this.ev.onError(code, message);
        return;

      case 'SERVER_RESTARTING':
        this.ev.onStatus({ kind: 'restarting' });
        return;

      case 'ROOM_NOT_FOUND': {
        // Two different events wearing one name. "You typed a code that doesn't
        // exist" is a typo, fixable in place. "The table you were playing at is
        // gone" is a bereavement and needs an ending.
        const wasPlaying = !!this.view && this.view.phase !== 'lobby';
        if (wasPlaying) {
          if (this.code) clearToken(this.code);
          this.closedByUs = true;
          this.sock?.close();
          this.ev.onStatus({ kind: 'ended', reason: 'roomGone', lastView: this.view });
          return;
        }
        if (this.code) clearToken(this.code);
        this.ev.onStatus({ kind: 'naming', code: this.code, error: code, suggestion: null });
        return;
      }

      case 'GAME_IN_PROGRESS':
        if (this.code) {
          this.ev.onStatus({ kind: 'waitingForNextGame', code: this.code });
          this.retryTimer = window.setTimeout(() => this.rejoin(), REJOIN_RETRY_MS);
        }
        return;

      case 'NAME_TAKEN':
        this.ev.onStatus({
          kind: 'naming',
          code: this.code,
          error: code,
          suggestion: `${this.name} 2`.slice(0, 14),
        });
        return;

      case 'ROOM_FULL':
      case 'BAD_INPUT':
        if (!this.view) {
          this.ev.onStatus({ kind: 'naming', code: this.code, error: code, suggestion: null });
          return;
        }
        this.ev.onError(code, message);
        return;

      default:
        this.ev.onError(code, message);
    }
  }

  private rejoin() {
    if (!this.code) return;
    if (this.sock?.readyState === WebSocket.OPEN) {
      this.send({ t: 'join', code: this.code, name: this.name, token: readToken(this.code) });
    } else {
      this.open();
    }
  }

  private terminal(reason: 'removed' | 'roomGone' | 'replaced') {
    if (reason === 'removed' && this.code) clearToken(this.code);
    this.ev.onStatus({ kind: 'ended', reason, lastView: this.view });
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeat = window.setInterval(() => this.send({ t: 'ping' }), PING_INTERVAL_MS);
    // The uplink can be half-open identically: the browser will happily leave a
    // WebSocket in OPEN with nothing flowing. If the pong stops coming, we are
    // the ones who have to notice.
    this.watchdog = window.setInterval(() => {
      if (Date.now() - this.lastPongAt > PING_INTERVAL_MS + 5_000) {
        this.sock?.close();
      }
    }, 5_000);
  }

  private stopHeartbeat() {
    if (this.heartbeat !== null) window.clearInterval(this.heartbeat);
    if (this.watchdog !== null) window.clearInterval(this.watchdog);
    this.heartbeat = null;
    this.watchdog = null;
  }

  private scheduleRetry(fixed?: number) {
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    const ladder = [1000, 2000, 4000, 8000, 10000];
    const base = fixed ?? ladder[Math.min(this.attempt, ladder.length - 1)]!;
    this.attempt += 1;
    // Full jitter. Every socket in the process dies at the same instant on a
    // deploy; without this they retry in lockstep forever at the 10s cap.
    const delay = Math.round(base * (0.5 + Math.random()));
    this.ev.onStatus({ kind: 'reconnecting' });
    this.retryTimer = window.setTimeout(() => this.open(), delay);
  }

  send(msg: ClientMsg) {
    if (this.sock?.readyState !== WebSocket.OPEN) return;
    try {
      this.sock.send(JSON.stringify(msg));
    } catch {
      /* the close handler will pick it up */
    }
  }

  close() {
    this.closedByUs = true;
    this.stopHeartbeat();
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    try {
      this.sock?.close();
    } catch {
      /* ignore */
    }
  }
}

export function errorText(code: ErrorCode): string {
  return ERROR_TEXT[code];
}
